import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from './api';
import { BrandHeader } from './Brand';
import { useI18n } from './i18n';
import SiteFooter from './SiteFooter';
import { trackSignup } from './analytics';
import { FoundingOffer } from './Founding';
import { CodeStep } from './TwoFactor';

// Google's own "G", as their sign-in button guidelines ask.
function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const GOOGLE_ERRORS = {
  cancelled: 'Google sign-in was cancelled.',
  failed: "Google sign-in didn't work. Please try again, or use your email and password.",
  unverified: "That Google account's email isn't verified yet. Verify it with Google, or use your email and password.",
  off: "Google sign-in isn't available right now. Please use your email and password.",
};

function AuthPage({ mode, onAuthenticated }) {
  const { t, tr, lang } = useI18n();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Two-step login: the password was right, now the code (twoFactor.js).
  const [ticket, setTicket] = useState(null);
  const isSignup = mode === 'signup';
  const [params, setParams] = useSearchParams();
  const [google, setGoogle] = useState(false);

  useEffect(() => {
    let live = true;
    api('/auth/google/enabled').then((r) => live && setGoogle(Boolean(r && r.enabled))).catch(() => {});
    return () => { live = false; };
  }, []);

  // Back from Google: an error to show, or the two-step code to ask for.
  useEffect(() => {
    const result = params.get('google');
    const code = params.get('ticket');
    if (!result && !code) return;
    if (GOOGLE_ERRORS[result]) setError(t(GOOGLE_ERRORS[result]));
    if (code) setTicket(code);
    setParams({}, { replace: true });
  }, [params, setParams, t]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  useEffect(() => {
    if (!params.get('google')) setError('');
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const body = isSignup ? { ...form, language: lang } : { email: form.email, password: form.password };
      const user = await api(`/auth/${mode}`, { method: 'POST', body });
      if (user && user.twoFactor === true && user.ticket) {
        setTicket(user.ticket);
        setSubmitting(false);
        return;
      }
      if (isSignup) trackSignup();
      onAuthenticated(user);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="App">
      <BrandHeader />

      <div className="auth-layout">
        <div className="page-title auth-intro">
          <p className="eyebrow">{t('Trading journal')}</p>
          <h1>{t('Every trade, on the record.')}</h1>
          <p className="subtitle">
            {t("Log trades or import your broker's CSV, and see your win rate and net P&L at a glance.")}
          </p>
        </div>

        {ticket ? <CodeStep ticket={ticket} onAuthenticated={onAuthenticated} onRestart={() => { setTicket(null); setForm((f) => ({ ...f, password: '' })); }} /> : (
        <form className="auth-card" onSubmit={handleSubmit}>
          <h2>{isSignup ? t('Create your free account') : t('Log in')}</h2>
          {isSignup && <p className="auth-free">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</p>}
          {isSignup && <FoundingOffer className="auth-founding" />}
          {google && (
            <>
              <a className="google-button" href={`/api/auth/google/start?from=${mode}&lang=${lang}`}>
                <GoogleLogo />
                <span>{t('Continue with Google')}</span>
              </a>
              <p className="auth-or"><span>{t('or')}</span></p>
            </>
          )}
          {isSignup && (
            <label>
              {t('Name')}
              <input name="name" value={form.name} onChange={handleChange} autoComplete="name" required />
            </label>
          )}
          <label>
            {t('Email')}
            <input name="email" type="email" value={form.email} onChange={handleChange} autoComplete="email" required />
          </label>
          <label>
            {t('Password')}
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              minLength={isSignup ? 8 : undefined}
              required
            />
          </label>
          {isSignup && <p className="field-hint">{t('At least 8 characters.')}</p>}
          {!isSignup && <Link to="/forgot-password" className="forgot-link">{t('Forgot password?')}</Link>}
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={submitting}>
            {submitting ? t('Please wait…') : isSignup ? t('Create account') : t('Log in')}
          </button>
          {isSignup && (
            <p className="auth-legal">
              {tr('By creating an account, you agree to the <0>Terms of Service</0> and <1>Privacy Policy</1>.', [
                (x) => <Link to="/terms">{x}</Link>, (x) => <Link to="/privacy">{x}</Link>,
              ])}
            </p>
          )}
          <p className="auth-switch">
            {isSignup ? t('Already have an account?') : t('New to Market Matrix?')}{' '}
            <Link to={isSignup ? '/login' : '/signup'}>
              {isSignup ? t('Log in') : t('Create an account')}
            </Link>
          </p>
        </form>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}

export default AuthPage;
